/**
 * Who is signed in, for as long as this tab is open.
 *
 * The access token is held **in memory only**. Not in `localStorage`, not in a
 * cookie, not in `sessionStorage`. Anything written to storage is readable by
 * any script that reaches this page, so a single cross-site scripting flaw
 * would hand over a working token rather than merely a defaced page. Keeping
 * it in a closure costs a silent renewal after a refresh, which is a fair
 * price.
 *
 * The token itself is obtained and renewed by the OIDC client; this store is
 * where the rest of the application reads it from, so nothing else needs to
 * know how sign-in works.
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { identityFromToken, MalformedToken, type Identity } from '@/auth/claims'
import { t } from '@/i18n'

/**
 * Who you are when authentication is switched off.
 *
 * Deliberately matches what the API hands back in the same state, so the
 * interface and the server agree about who is asking and what they may do.
 * Obviously synthetic, so it can never be mistaken for a real account.
 */
const DEVELOPMENT_IDENTITY: Identity = {
  id: 'dev-user',
  username: 'dev',
  email: 'dev@localhost',
  name: 'Development User',
  roles: new Set(['DAF']),
  // Never expires: there is no provider to renew against.
  expiresAt: 0,
}

/** Renew this long before expiry, so a request never races the clock. */
const RENEW_MARGIN_MS = 60_000

export const useSessionStore = defineStore('session', () => {
  const token = ref<string | null>(null)
  const identity = ref<Identity | null>(null)
  const problem = ref<string | null>(null)

  /**
   * Whether a session is still being sought.
   *
   * Starts true, and that is the point of it. Nothing about the session is
   * known until the portal has been asked, so a screen saying "sign in" shown
   * before the attempt finishes is telling somebody who is signed in that they
   * are not. It also covers the seconds between deciding to redirect and the
   * browser actually leaving, which would otherwise be spent looking at a
   * prompt nobody is meant to read.
   */
  const establishing = ref(true)

  // An identity, not a token: with authentication off there is no token and
  // somebody is still signed in.
  const isSignedIn = computed(() => identity.value !== null)

  /** The name to show. Never blank while somebody is signed in. */
  const displayName = computed(() => identity.value?.name ?? '')

  /** Every name this person may be recorded under on a report. */
  const handles = computed<readonly string[]>(() => {
    const current = identity.value
    if (!current) return []
    const found = new Set<string>()
    if (current.username) found.add(current.username.toLowerCase())
    if (current.email) found.add(current.email.toLowerCase())
    return [...found]
  })

  const roles = computed<ReadonlySet<string>>(() => identity.value?.roles ?? new Set())

  function hasRole(role: string): boolean {
    return roles.value.has(role)
  }

  /** Whether a report belongs to the person signed in. */
  function owns(owner: string): boolean {
    return handles.value.includes(owner.toLowerCase())
  }

  /**
   * Sign in as nobody in particular, for running without an identity provider.
   *
   * There is no token, so nothing is attached to a request. The API is in the
   * same state and identifies the caller the same way.
   */
  function signedInWithoutAuthentication(as: Identity = DEVELOPMENT_IDENTITY): void {
    token.value = null
    identity.value = as
    problem.value = null
  }

  const isExpired = computed(() => {
    const expiry = identity.value?.expiresAt ?? 0
    return expiry === 0 || Date.now() >= expiry
  })

  /** Whether the token is close enough to expiry to be worth renewing. */
  const needsRenewal = computed(() => {
    const expiry = identity.value?.expiresAt ?? 0
    return expiry === 0 || Date.now() >= expiry - RENEW_MARGIN_MS
  })

  /**
   * Accept a token obtained from the portal.
   *
   * An already-expired token is refused rather than stored. It would fail on
   * the first request anyway, and storing it would make the interface claim
   * somebody is signed in while nothing they do works.
   */
  function signedIn(accessToken: string): boolean {
    let read: Identity
    try {
      read = identityFromToken(accessToken)
    } catch (error) {
      // The exception's own message is written for a log, not for a person.
      problem.value = error instanceof MalformedToken ? error.message : t('common.tokenUnreadable')
      signedOut()
      return false
    }

    if (read.expiresAt !== 0 && Date.now() >= read.expiresAt) {
      problem.value = t('common.tokenExpired')
      signedOut()
      return false
    }

    token.value = accessToken
    identity.value = read
    problem.value = null
    return true
  }

  function signedOut(): void {
    token.value = null
    identity.value = null
  }

  /**
   * Nothing further is being attempted.
   *
   * Called once the search for a session has finished, whether it found one or
   * gave up. Only then is "not signed in" a fact worth putting on the screen.
   */
  function settled(): void {
    establishing.value = false
  }

  /** The current token, for the HTTP client to attach. */
  function currentToken(): string | null {
    return token.value
  }

  return {
    token,
    identity,
    problem,
    establishing,
    isSignedIn,
    displayName,
    handles,
    roles,
    isExpired,
    needsRenewal,
    hasRole,
    owns,
    signedIn,
    signedInWithoutAuthentication,
    signedOut,
    settled,
    currentToken,
  }
})
